<template>
  <div>
    <!-- Cancel Confirmation Dialog -->
    <Dialog :open="isCancelPopup" @update:open="isCancelPopup = $event">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{{ t('common.forms.delete') }}</DialogTitle>
        </DialogHeader>
        <p class="tw-text-sm tw-text-neutral-600">Are you sure you want to discard this order?</p>
        <DialogFooter>
          <Button variant="secondary" @click="closePopup">
            {{ t('common.forms.cancel') }}
          </Button>
          <Button @click="onChangeOrderCancel">
            {{ t('common.forms.cancel_discard') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- New Supplier Dialog -->
    <Dialog :open="isSupplierPopup" @update:open="isSupplierPopup = $event">
      <DialogContent class="sm:tw-max-w-[800px]">
        <DialogHeader>
          <DialogTitle>{{ t('orders.titles.add_supplier') }}</DialogTitle>
        </DialogHeader>
        <form @submit.prevent="addNewSupplier">
          <!-- Name and Number -->
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
            <div>
              <Label>
                {{ t('suppliers.titles.supplier_name') }}
                <span class="tw-text-red-600">*</span>
              </Label>
              <Input
                v-model="supplierName"
                name="supplierName"
                type="text"
                :class="{ 'tw-border-danger': errors.supplierName }"
                :placeholder="t('suppliers.titles.name_placeholder') + '...'"
              />
              <div v-if="errors.supplierName" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.supplierName }}
              </div>
            </div>
            <input hidden :value="formData.customer" />
            <div>
              <Label>
                {{ t('suppliers.titles.supplier_number') }}
                <span class="tw-text-red-600">*</span>
              </Label>
              <Input
                v-model="supplierNumber"
                name="supplierNumber"
                type="text"
                :class="{ 'tw-border-danger': errors.supplierNumber }"
                :placeholder="t('suppliers.titles.number_placeholder') + '...'"
              />
              <div v-if="errors.supplierNumber" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.supplierNumber }}
              </div>
            </div>
          </div>
          <!-- Address and Zip -->
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
            <div>
              <Label>{{ t('suppliers.titles.address') }}</Label>
              <Input
                v-model="address"
                name="address"
                type="text"
                :class="{ 'tw-border-danger': errors.address }"
                :placeholder="t('suppliers.titles.address_placeholder') + '...'"
              />
              <div v-if="errors.address" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.address }}
              </div>
            </div>
            <div>
              <Label>{{ t('suppliers.titles.zip_code') }}</Label>
              <Input
                v-model="zipCode"
                name="zipCode"
                type="text"
                :class="{ 'tw-border-danger': errors.zipCode }"
                :placeholder="t('suppliers.titles.zip_code_placeholder') + '...'"
              />
              <div v-if="errors.zipCode" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.zipCode }}
              </div>
            </div>
          </div>
          <!-- Country and City -->
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
            <div>
              <Label>{{ t('suppliers.titles.country') }}</Label>
              <SearchableSelect
                v-model="country"
                :options="countriesList"
                value-prop="name"
                label-prop="displayed_name"
                :placeholder="t('suppliers.titles.country_placeholder') + '...'"
                :class="{ 'tw-border-danger': errors.country }"
              />
              <div v-if="errors.country" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.country }}
              </div>
            </div>
            <div>
              <Label>{{ t('suppliers.titles.city') }}</Label>
              <SearchableSelect
                v-model="city"
                :options="citiesList"
                value-prop="name"
                label-prop="name"
                :placeholder="t('suppliers.titles.city_placeholder') + '...'"
                :class="{ 'tw-border-danger': errors.city }"
              />
              <div v-if="errors.city" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.city }}
              </div>
            </div>
          </div>
          <!-- Phone and Email -->
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
            <div>
              <Label>{{ t('suppliers.titles.phone') }}</Label>
              <PhoneInput
                v-model="phone"
                :placeholder="t('suppliers.titles.phone_placeholder')"
                code="DE"
                :disabled="false"
                :class="{ 'tw-border-danger': errors.phone }"
              />
              <div v-if="errors.phone" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.phone }}
              </div>
            </div>
            <div>
              <Label>{{ t('suppliers.titles.email') }}</Label>
              <Input
                v-model="supplierEmail"
                name="supplierEmail"
                type="email"
                :class="{ 'tw-border-danger': errors.supplierEmail }"
                :placeholder="t('suppliers.titles.email_placeholder') + '...'"
              />
              <div v-if="errors.supplierEmail" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.supplierEmail }}
              </div>
            </div>
          </div>
          <!-- Supplier Alerts -->
          <div v-if="supplierCreatedSuccessfully != null" class="tw-mb-4">
            <Alert v-if="supplierCreatedSuccessfully && !meta.dirty" class="lg:tw-w-7/12">
              <AlertDescription>{{ t('suppliers.titles.create_success') }}</AlertDescription>
            </Alert>
            <Alert v-if="!supplierCreatedSuccessfully" variant="destructive" class="lg:tw-w-7/12">
              <AlertDescription>{{ errorSupplierMessage }}</AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" @click="closeNewSupplierModal">
              {{ t('common.forms.cancel') }}
            </Button>
            <CustomerActionButton
              :disabled="isSubmitting"
              type="submit"
              class="tw-bg-primary-500 tw-text-secondary-900 tw-font-semibold hover:tw-bg-primary-600 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-items-center tw-gap-2"
            >
              <Loader2 v-if="isSubmitting" class="tw-h-4 tw-w-4 tw-animate-spin" />
              {{ t('suppliers.titles.add_supplier') }}
            </CustomerActionButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <!-- Sticky action bar -->
    <FormActionBar :title="t('customer_management.titles.orders')">
      <template #meta>
        <span
          v-if="
            currentUser?.is_admin ||
            currentUser?.user_role.includes('Purchaser') ||
            currentUser?.user_role.includes('Approver') ||
            currentUser?.user_role.includes('Order Clearer')
          "
          class="tw-hidden tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-surface-200 tw-bg-white tw-px-3 tw-py-1.5 sm:tw-inline-flex dark:tw-border-neutral-800 dark:tw-bg-neutral-950"
        >
          <span class="tw-text-xs tw-text-surface-500 dark:tw-text-neutral-400">
            {{ t('orders.titles.remaining_balance') }}
          </span>
          <span
            class="tw-text-sm tw-font-semibold tw-tabular-nums"
            :class="currentBalance >= 0 ? 'tw-text-emerald-600' : 'tw-text-red-600'"
          >
            {{ n(currentBalance, 'currency') }}
          </span>
        </span>
      </template>

      <template #actions>
        <Button
          type="button"
          variant="outline"
          size="sm"
          :disabled="isMainSubmitting || isSavingDraft"
          @click="onCancel()"
        >
          {{ t('common.forms.cancel_discard') }}
        </Button>
        <CustomerActionButton
          v-if="!isEditOrder || (isEditOrder && !orders[0]?.is_clarification_requested)"
          :loading="isSavingDraft"
          class="tw-rounded-md tw-bg-secondary-500 tw-px-3.5 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-secondary-600"
          @click="runDraft"
        >
          {{ t('orders.forms.save_draft') }}
        </CustomerActionButton>
        <CustomerActionButton
          class="tw-rounded-md tw-bg-primary-500 tw-px-3.5 tw-py-2 tw-text-sm tw-font-semibold tw-text-secondary-900 hover:tw-bg-primary-600"
          :loading="isMainSubmitting"
          :disabled="isSubmitDisabled || isMainSubmitting"
          @click.prevent="runSubmit"
        >
          {{ currentUser?.is_admin ? t('common.forms.submit_sign') : t('common.forms.submit') }}
        </CustomerActionButton>
      </template>

      <template #banner>
        <div
          v-if="
            isCreatedSuccessfully != null ||
            isEditSuccessfully !== null ||
            isDraftSuccessfully !== null ||
            isUploadError
          "
          class="tw-mt-3 tw-space-y-2"
        >
          <template
            v-if="
              isCreatedSuccessfully != null ||
              isEditSuccessfully !== null ||
              isDraftSuccessfully !== null
            "
          >
            <Alert v-if="isCreatedSuccessfully" class="lg:tw-w-7/12">
              <AlertDescription>
                {{ t('customer_management.messages.order_forms_create_success') }}
              </AlertDescription>
            </Alert>
            <Alert v-else-if="isEditSuccessfully" class="lg:tw-w-7/12">
              <AlertDescription>Edit Order Successfully</AlertDescription>
            </Alert>
            <Alert v-else-if="isDraftSuccessfully" class="lg:tw-w-7/12">
              <AlertDescription>Saved as draft</AlertDescription>
            </Alert>
            <Alert v-else variant="destructive" class="lg:tw-w-7/12">
              <AlertDescription>{{ errorMessage }}</AlertDescription>
            </Alert>
          </template>
          <Alert v-if="isUploadError" variant="destructive" class="lg:tw-w-5/12">
            <AlertDescription>Max. size of file is 20 MB</AlertDescription>
          </Alert>
        </div>
      </template>
    </FormActionBar>

    <!-- No Customer Selected -->
    <div
      v-if="formData.customer === null"
      class="tw-py-10 tw-text-center tw-text-sm tw-text-surface-500 dark:tw-text-neutral-400"
    >
      Please select a customer to view the related order fields
    </div>

    <div v-else>
      <!-- Per-Order Cards -->
      <div v-if="orders && orders.length">
        <EntitySplitLayout
          v-for="(item, index) in formData.orderCount"
          :key="index"
          storage-key="recurring-form-split-size-v2"
          :default-document-size="67"
          :min-document-size="50"
          :max-document-size="67"
        >
          <template #documents>
            <DocumentManagerPanel
              :files="getExistingFiles(index, orders[index].files || [])"
              :pending-files="getPendingFiles(index)"
              :allow-remove-existing="false"
              editable
              @add="files => addFiles(files, index)"
              @remove="removeFile"
            />
            <input
              type="file"
              :id="`take-picture-${index}`"
              class="tw-hidden"
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              capture="environment"
              multiple
              @change="event => handleCameraCapture(event, index)"
            />
          </template>
          <template #details>
            <div class="tw-pr-2 lg:tw-flex-1 lg:tw-min-h-0 lg:tw-overflow-y-auto">
              <!-- Section: Project -->
              <FormSectionHeader :title="t('requests.titles.project')" first>
                <ProjectSelect
                  v-model="formData.project"
                  :customer-id="newCustomerId"
                  :placeholder="t('orders.forms.select_project')"
                  class="tw-max-w-md"
                />
              </FormSectionHeader>

              <!-- Section: Recurring Info -->
              <FormSectionHeader
                :title="
                  currentUser?.is_admin
                    ? t('orders.titles.order_info')
                    : t('orders.recurring.recurring')
                "
              >
                <!-- Row: Order Name + Start Date -->
                <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                  <div>
                    <Label>{{ t('orders.titles.order_name') }}</Label>
                    <Input
                      type="text"
                      v-model="formData.name"
                      :placeholder="t('orders.recurring.enter_order_name')"
                    />
                  </div>
                  <div>
                    <Label>{{ t('orders.titles.start_date') }}</Label>
                    <DatePicker
                      v-model="formData.startDate"
                      :placeholder="t('orders.forms.select_date')"
                    />
                  </div>
                </div>
                <!-- Row: Frequency + Ends After -->
                <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                  <div>
                    <Label>{{ t('orders.recurring.frequency') }}</Label>
                    <Select v-model="formData.frequency">
                      <SelectTrigger>
                        <SelectValue :placeholder="t('orders.recurring.select_frequency')" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DAILY">
                          {{ t('orders.recurring.frequency_daily') }}
                        </SelectItem>
                        <SelectItem value="WEEKLY">
                          {{ t('orders.recurring.frequency_weekly') }}
                        </SelectItem>
                        <SelectItem value="MONTHLY">
                          {{ t('orders.recurring.frequency_monthly') }}
                        </SelectItem>
                        <SelectItem value="QUARTERLY">
                          {{ t('orders.recurring.frequency_quarterly') }}
                        </SelectItem>
                        <SelectItem value="YEARLY">
                          {{ t('orders.recurring.frequency_yearly') }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{{ t('orders.recurring.ends_after') }}</Label>
                    <div class="tw-flex tw-items-center tw-gap-2">
                      <span class="tw-text-sm tw-text-neutral-500 tw-whitespace-nowrap">
                        {{ t('orders.recurring.ends_after_label') }}
                      </span>
                      <Input
                        type="number"
                        min="1"
                        v-model="formData.endsAfter"
                        :placeholder="t('orders.recurring.enter_ends_after')"
                      />
                    </div>
                  </div>
                </div>
              </FormSectionHeader>

              <!-- Section: Supplier Details -->
              <FormSectionHeader
                :title="t('orders.sections.supplier_details')"
                :force-expanded="isErrorFound"
              >
                <!-- Row: Preliminary Insolvency Proceedings (full width) -->
                <div class="tw-mb-4">
                  <Label>{{ t('orders.titles.prem_proc') }}</Label>
                  <div class="tw-flex tw-gap-4 tw-mt-1">
                    <label class="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm">
                      <input
                        type="radio"
                        v-model="orders[index].preliminary_insolvency_proceedings"
                        :name="`preliminary-proceeding-${index}`"
                        :value="true"
                        class="tw-mr-1"
                        style="accent-color: rgb(var(--secondary-500))"
                      />
                      {{ t('common.labels.yes') }}
                    </label>
                    <label class="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm">
                      <input
                        type="radio"
                        v-model="orders[index].preliminary_insolvency_proceedings"
                        :name="`preliminary-proceeding-${index}`"
                        :value="false"
                        class="tw-mr-1"
                        style="accent-color: rgb(var(--secondary-500))"
                      />
                      {{ t('common.data.number_title') }}
                    </label>
                  </div>
                </div>

                <!-- Row: Supplier + Supplier Order Number -->
                <div class="tw-mb-4 tw-space-y-4">
                  <div>
                    <Label>
                      {{ t('orders.titles.supplier') }}
                      <span class="tw-text-red-600">*</span>
                    </Label>
                    <div class="tw-flex tw-gap-2">
                      <SupplierSelect
                        ref="supplierSelectRef"
                        class="tw-flex-1"
                        v-model="orders[index].supplier"
                        :customer-id="newCustomerId"
                        :placeholder="t('orders.forms.select_supplier')"
                        :action-label="t('orders.titles.add_supplier')"
                        @action="openNewSupplierModal(index)"
                        @change="(s: any) => { if (s) orders[index].order_no_supplier = s.supplier_number; }"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        class="tw-h-9 tw-w-9 tw-shrink-0"
                        @click="openNewSupplierModal(index)"
                      >
                        <Plus class="tw-h-4 tw-w-4" />
                      </Button>
                    </div>
                    <div
                      v-if="isErrorFound && orderErrors[index].supplier !== ''"
                      class="tw-text-danger tw-text-xs tw-mt-1"
                    >
                      {{ orderErrors[index].supplier }}
                    </div>
                  </div>
                  <div>
                    <Label>
                      {{ t('orders.titles.supplier_no') }}
                      <span class="tw-text-red-600">*</span>
                    </Label>
                    <Input
                      type="text"
                      readonly
                      v-model="orders[index].order_no_supplier"
                      class="!tw-bg-neutral-100 !tw-text-neutral-500 !tw-cursor-default !tw-border-neutral-200/50 !tw-shadow-none"
                    />
                  </div>
                </div>
              </FormSectionHeader>

              <!-- Section: Amounts -->
              <FormSectionHeader
                :title="t('orders.sections.amounts')"
                :force-expanded="isErrorFound"
              >
                <!-- Net / VAT / Gross field group with auto-calculation -->
                <NetVatGrossFields
                  v-model:net="orders[index].net_eur"
                  v-model:gross="orders[index].amount"
                  v-model:vat-rate="orders[index].vat_rate"
                  :net-error="isErrorFound ? orderErrors[index].net_eur : ''"
                  :gross-error="isErrorFound ? orderErrors[index].amount : ''"
                />
              </FormSectionHeader>

              <!-- Section: Additional Details (dynamic fields) -->
              <FormSectionHeader
                v-if="dynamicFieldPairs(index).length > 0"
                :title="t('orders.sections.additional_details')"
                :force-expanded="isErrorFound"
              >
                <!-- Dynamic Form Fields: rendered in paired rows -->
                <template v-for="(pair, pairIdx) in dynamicFieldPairs(index)" :key="pairIdx">
                  <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                    <div v-for="field in pair" :key="field.key">
                      <Label>
                        {{ field.label }}
                        <span class="tw-text-red-600" v-if="field.mandatory">*</span>
                      </Label>
                      <!-- TEXT / INTEGER -->
                      <Input
                        v-if="field.dataType === 'TEXT' || field.dataType === 'INTEGER'"
                        type="text"
                        :placeholder="'Enter ' + field.label"
                        v-model="orders[index][field.key]"
                      />
                      <!-- DATE -->
                      <DatePicker
                        v-else-if="field.dataType === 'DATE'"
                        :placeholder="'Enter ' + field.label"
                        v-model="orders[index][field.key]"
                      />
                      <!-- BOOLEAN -->
                      <div
                        v-else-if="field.dataType === 'BOOLEAN'"
                        class="tw-flex tw-gap-4 tw-mt-1"
                      >
                        <label
                          class="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm"
                        >
                          <input
                            type="radio"
                            :name="`${field.key}-${index}`"
                            :value="true"
                            v-model="orders[index][field.key]"
                            class="tw-mr-1"
                            style="accent-color: rgb(var(--secondary-500))"
                          />
                          {{ t('common.labels.yes') }}
                        </label>
                        <label
                          class="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm"
                        >
                          <input
                            type="radio"
                            :name="`${field.key}-${index}`"
                            :value="false"
                            v-model="orders[index][field.key]"
                            class="tw-mr-1"
                            style="accent-color: rgb(var(--secondary-500))"
                          />
                          {{ t('common.data.number_title') }}
                        </label>
                      </div>
                      <!-- Error -->
                      <div
                        v-if="isErrorFound && orderErrors[index][field.key] !== ''"
                        class="tw-text-danger tw-text-xs tw-mt-1"
                      >
                        {{ orderErrors[index][field.key] }}
                      </div>
                    </div>
                  </div>
                </template>
              </FormSectionHeader>

              <!-- Section: Service Period -->
              <FormSectionHeader
                :title="t('orders.sections.service_period')"
                :force-expanded="isErrorFound"
              >
                <!-- Row: Service Date -->
                <div class="tw-mb-4">
                  <div>
                    <Label>
                      {{ t('orders.titles.service_date') }}
                      <span class="tw-text-red-600">*</span>
                    </Label>
                    <DatePicker
                      :placeholder="t('orders.titles.enter_service_date')"
                      v-model="orders[index].service_date"
                    />
                    <div
                      v-if="isErrorFound && orderErrors[index].service_date !== ''"
                      class="tw-text-danger tw-text-xs tw-mt-1"
                    >
                      {{ orderErrors[index].service_date }}
                    </div>
                  </div>
                </div>
              </FormSectionHeader>
            </div>
          </template>
        </EntitySplitLayout>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, type Ref, ref, watch } from 'vue';

import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { Card } from '@/components/ui/card';
import EntitySplitLayout from '@/components/shared/entity-detail/EntitySplitLayout.vue';
import FormActionBar from '@/components/shared/forms/FormActionBar.vue';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2, Plus, Loader2, RefreshCw } from 'lucide-vue-next';
import { useCustomerStore } from '@/stores/customer.store';
import { useUserStore } from '@/stores/user.store';
import { useSupplierStore } from '@/stores/supplier.store';

import { storeToRefs } from 'pinia';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import NetVatGrossFields from '@/components/shared/net-vat-gross/NetVatGrossFields.vue';
import { PhoneInput } from '@/components/ui/phone-input';
import { FormSectionHeader } from '@/components/ui/form-section-header';
import { DocumentManagerPanel } from '@/components/shared/documents';
import { useEntityAttachments } from '@/composables/useEntityAttachments';
import router from '@/router';
import { useRouter, useRoute } from 'vue-router';
const route = useRoute();
import axios from 'axios';
import moment from 'moment';
import { useOrderStore } from '@/stores/order.store';
import { useSupplierForm } from '@/_helpers/composables/master-data/supplier-changing.composable';
import { useWorldStore } from '@/stores/world.store';
import { useRecurringInstructionStore } from '@/stores/recurring-instructions.store';
import { useI18n } from 'vue-i18n';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import cookieService from '@/_helpers/services/cookie.service';

const { t, n } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();

const { getCustomersOptions } = useCustomerStore();
const { getCustomerCurrentBalance } = useOrderStore();
const { createRecurringInstruction, getRecurringInstructionById, updateRecurringInstruction } =
  useRecurringInstructionStore();

const { getExistingFiles, getPendingFiles, addFiles, removeFile, getPendingUploadsForIndex } =
  useEntityAttachments();

const getRecurringUploadFiles = (index = 0) =>
  getPendingUploadsForIndex(index).map(item => item.file);

const handleCameraCapture = (event: Event, index: number) => {
  const target = event.target as HTMLInputElement;
  if (target.files?.length) {
    addFiles(Array.from(target.files), index);
    target.value = '';
  }
};
const { getSuppliersList, createSupplier } = useSupplierStore();
const { currentUser } = storeToRefs(useUserStore());
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';
import { SearchableSelect } from '@/components/ui/searchable-select';
import SupplierSelect from '@/components/shared/SupplierSelect.vue';
import ProjectSelect from '@/components/shared/ProjectSelect.vue';

const { getCitiesListByCountry } = useWorldStore();

const {
  errors,
  handleSubmit,
  isSubmitting,
  meta,
  resetForm,
  supplierName,
  supplierNumber,
  address,
  zipCode,
  country,
  city,
  resetCity,
  phone,
  supplierEmail,
  countriesList,
  citiesList,
  setFieldValue,
} = useSupplierForm();

let customersList: any = ref([]);

const supplierSelectRef = ref<any>(null);

const isCancelPopup = ref(false);

const isSupplierPopup = ref(false);

const isEditOrder = ref(false);

const isUploadError = ref(false);

const formFields: any = ref([]);

const currentBalance: any = ref(0);

const newCustomerId: any = ref(null);

let orders: any = ref([
  {
    no: '',
    date_order_list: null,
    document_no_internal: null,
    order_type: null,
    delivery_date: null,
    order_triggered: false,
    order_triggered_on: null,
    vendor_no: '',
    supplier: null,
    order_no_supplier: '',
    range_in_months: null,
    order_related_purchase_order: null,
    project_no: null,
    expected_date_processing: null,
    comment: null,
    net_eur: null,
    gross_eur: null,
    vat_rate: null,
    release_effected: null,
    comment_allea: null,
    comment_management: null,
    comment_insv: null,
    preliminary_insolvency_proceedings: true,
    service_date: '',
    status: 'DRAFT',
    project: null,
    is_recurring: false,
    amount: null,
    attachedDocument: null,
    files: [],
    is_clarification_requested: false,
  },
]);

let isErrorFound = ref(false);

let orderErrors: any = ref([
  {
    no: '',
    date_order_list: '',
    document_no_internal: '',
    order_type: '',
    delivery_date: '',
    order_triggered: '',
    order_triggered_on: '',
    vendor_no: '',
    supplier: '',
    order_no_supplier: '',
    range_in_months: '',
    order_related_purchase_order: '',
    project_no: '',
    expected_date_processing: '',
    comment: '',
    net_eur: '',
    gross_eur: '',
    release_effected: '',
    comment_allea: '',
    comment_management: '',
    comment_insv: '',
    preliminary_insolvency_proceedings: '',
    service_date: '',
    status: '',
    project: '',
    is_recurring: '',
    amount: '',
    attachedDocument: '',
  },
]);

const orderFiles = ref([
  {
    attachedDocument: null,
    document: null,
    url: '',
  },
]);

let fileCount = ref(0);

let editOrderId = ref(0);

const newFiles: any = ref([
  {
    file: null,
    index: null,
    file_name: null,
    file_size: null,
  },
]);

let formData: any = reactive({
  customer: null,
  project: null,
  orderCount: 1,
  name: '',
  startDate: null,
  frequency: '',
  endsAfter: 0,
});

const isCreatedSuccessfully: Ref<boolean | null> = ref(null);

const isEditSuccessfully: Ref<boolean | null> = ref(null);

const isDraftSuccessfully: Ref<boolean | null> = ref(null);

const errorMessage: any = ref('');

const supplierCreatedSuccessfully: Ref<boolean | null> = ref(null);
const errorSupplierMessage = ref('');

const orderSupplierIndex = ref(0);

const rules = {
  customer: {
    required,
  },
};

const $v = useVuelidate(rules, formData);

watch(
  () => formData.customer,
  async (newVal, oldVal) => {
    if (!newVal) return;
    formFields.value = [];
    await axios
      .get(`/orders/customer_order_fields/?customer=${newVal}`)
      .then(res => {
        res.data.results.map((item: any) => {
          formFields.value.push(item);
        });

        /* sort array */
        const fieldOrder = {
          document_no_internal: 1,
          project_no: 2,
          order_triggered: 3,
          order_triggered_on: 4,
          expected_date_processing: 5,
          range_in_months: 6,
          date: 7,
          comment: 8,
        };
        formFields.value.sort(
          (a, b) =>
            (fieldOrder[a.field.model_field_name] ?? 50) -
            (fieldOrder[b.field.model_field_name] ?? 50)
        );
        /* remove Order No. Supplier  */
        formFields.value = formFields.value.filter(
          (item: any) => item.field.model_field_name !== 'order_no_supplier'
        );
      })
      .catch(error => console.log(error));
    newCustomerId.value = newVal;
    if (newVal !== null) {
      await getCustomerCurrentBalance(newVal, null).then(response => {
        currentBalance.value = response.current_balance;
      });
    }

    if (oldVal !== null) {
      // empty supplier selection
      orders.value = orders.value.map((order: any) => {
        return { ...order, supplier: null };
      });
    }
  }
);

watch(
  () => formData.project,
  async newVal => {
    if (newCustomerId.value !== null) {
      await getCustomerCurrentBalance(newCustomerId.value, newVal).then(response => {
        currentBalance.value = response.current_balance;
      });
    }
  }
);

/* Choose Document */

const chooseDocument = (event: any, index: number) => {
  let fileToUpload = event.target.files[0];
  let size = fileToUpload.size / 1024;
  if (size > 20000) {
    isUploadError.value = true;
    return;
  }
  isUploadError.value = false;
  orderFiles.value[index].attachedDocument = fileToUpload;
  orderFiles.value[index].document = fileToUpload;

  var reader = new FileReader();
  reader.readAsDataURL(fileToUpload);
  if (fileToUpload.type.includes('pdf')) {
    reader.onload = async () => {
      let response = await fetch(`${reader.result}`);
      let pdfBlob = await response.blob();

      const blobUrl = URL.createObjectURL(pdfBlob);

      orderFiles.value[index].url =
        "<embed src='" +
        blobUrl +
        "' type='application/pdf' frameBorder='0' scrolling='auto' width='100%' height='700px' />";
    };
  }
  if (fileToUpload.type.includes('image')) {
    reader.onload = async () => {
      let response = await fetch(`${reader.result}`);
      let pdfBlob = await response.blob();

      const blobUrl = URL.createObjectURL(pdfBlob);
      orderFiles.value[index].url =
        "<img src='" + blobUrl + "' alt='preview' style='width: 100%' class='img-fluid' />";
    };
  }
  onUploadDocx(index, fileToUpload.name, fileToUpload.size);
};

/* Close Preview Document */
const closePreview = (index: number) => {
  orderFiles.value[index].attachedDocument = null;
  orderFiles.value[index].document = null;
  orderFiles.value[index].url = '';

  newFiles.value.forEach(item => {
    if (item.index === index) {
      newFiles.value.pop(item);
    }
  });
};

const isSubmitDisabled = computed(() => {
  let flag = true;
  orders.value.forEach((order: any) => {
    if (
      order.supplier !== null &&
      order.net_eur !== null &&
      order.amount !== null &&
      order.service_date !== ''
    ) {
      flag = false;
    }
  });
  return flag;
});

/* Mapping all errors messages to respected field */
const showErrorMessageToField = (errors: []) => {
  errors.map((error: any) => {
    if (error.attr.includes('.')) {
      let array = error.attr.split('.');
      let errorOrderIndex = array[0];
      let errorOrderField = array[1];
      let message = error.detail;

      orderErrors.value[errorOrderIndex][errorOrderField] = message;
    } else {
      isEditSuccessfully.value = false;
      errorMessage.value = `${error.attr} ${error.detail}`;
    }
  });
};

const cleanOrders = (submit: boolean = false) => {
  // prepare order data, adding in all the fields required by the backend
  orders.value.map((item: any, _) => {
    item.submit = submit;
    item.project = formData.project;
    item.customer = formData.customer;
    item.end_after = formData.endsAfter;
    item.frequency = formData.frequency;
    item.name = formData.name;
    item.start_date =
      formData.startDate !== null ? moment(formData.startDate).format('YYYY-MM-DD') : null;
    item.date_order_list =
      item.date_order_list !== null ? moment(item.date_order_list).format('YYYY-MM-DD') : null;
    item.delivery_date =
      item.delivery_date !== null ? moment(item.delivery_date).format('YYYY-MM-DD') : null;
    item.order_triggered_on =
      item.order_triggered_on !== null
        ? moment(item.order_triggered_on).format('YYYY-MM-DD')
        : null;
    item.service_date =
      item.service_date === '' || item.service_date === null
        ? null
        : moment(item.service_date).format('YYYY-MM-DD');
    item.expected_date_processing = item.expected_date_processing
      ? moment(item.expected_date_processing).format('YYYY-MM-DD')
      : item.expected_date_processing;
    item.release_effected = item.release_effected
      ? moment(item.release_effected).format('YYYY-MM-DD')
      : item.expected_date_processing;
  });
};

const removeAttachment = (index: number) => {
  orders.value[index].files.shift();
  orders.value[index].remove_files = true;
};
/* Submit the data */
const onSubmit = async () => {
  isErrorFound.value = false;
  isCreatedSuccessfully.value = null;
  cleanOrders(true);

  /* Check Validations */
  $v.value.$touch();

  if (!$v.value.$invalid) {
    try {
      let data = await createRecurringInstruction({
        ...orders.value[0],
        files: getRecurringUploadFiles(0),
      });
      if (data) {
        isCreatedSuccessfully.value = true;
        setTimeout(() => {
          router.push({ name: 'recurring-orders' });
        }, 1000);
      } else {
        isCreatedSuccessfully.value = false;
      }
    } catch (error: any) {
      if (error.response.data.type === 'validation_error') {
        isErrorFound.value = true;
        showErrorMessageToField(error.response.data.errors);
      } else {
        errorMessage.value = error.response.data.errors[0].detail;
        isCreatedSuccessfully.value = false;
      }
    }
  }
};

const onEdit = async () => {
  cleanOrders(true);

  try {
    let data = await updateRecurringInstruction(editOrderId.value, {
      ...orders.value[0],
      files: getRecurringUploadFiles(0),
    });
    if (data) {
      isEditSuccessfully.value = true;
      setTimeout(() => {
        router.push({ name: 'recurring-orders' });
      }, 1000);
    } else {
      isEditSuccessfully.value = false;
    }
  } catch (error: any) {
    if (error.response.data.type === 'validation_error') {
      isErrorFound.value = true;
      showErrorMessageToField(error.response.data.errors);
    } else {
      errorMessage.value = error.response.data.errors[0].detail;
      isEditSuccessfully.value = false;
      setTimeout(() => {
        isEditSuccessfully.value = null;
      }, 2000);
    }
  }
};

const closePopup = () => {
  isCancelPopup.value = false;
};

const onChangeOrderCancel = async () => {
  closePopup();
  router.push({ name: 'recurring-orders' });
};

/* on Cancel */
const onCancel = () => {
  isCancelPopup.value = true;
};

/* on Draft */
const onDraft = async () => {
  cleanOrders(false);
  try {
    let data = await createRecurringInstruction({
      ...orders.value[0],
      files: getRecurringUploadFiles(0),
    });
    if (data) {
      isDraftSuccessfully.value = true;
      setTimeout(() => {
        router.push({ name: 'recurring-orders', query: { status: 'DRAFT' } });
      }, 1000);
    } else {
      isDraftSuccessfully.value = false;
    }
  } catch (error: any) {
    if (error.response.data.type === 'validation_error') {
      isErrorFound.value = true;
      showErrorMessageToField(error.response.data.errors);
    } else {
      errorMessage.value = error.response.data.errors[0].detail;
      isDraftSuccessfully.value = false;
      setTimeout(() => {
        isDraftSuccessfully.value = null;
      }, 2000);
    }
  }
};

/* on Draft Edit */
const onDraftEdit = async () => {
  cleanOrders(false);
  try {
    let data = await updateRecurringInstruction(editOrderId.value, {
      ...orders.value[0],
      files: getRecurringUploadFiles(0),
    });
    if (data) {
      isEditSuccessfully.value = true;
      setTimeout(() => {
        router.push({ name: 'recurring-orders' });
      }, 1000);
    }
  } catch (error: any) {
    if (error.response.data.type === 'validation_error') {
      isErrorFound.value = true;
      showErrorMessageToField(error.response.data.errors);
    } else {
      errorMessage.value = error.response.data.errors[0].detail;
      isEditSuccessfully.value = false;
      setTimeout(() => {
        isEditSuccessfully.value = null;
      }, 2000);
    }
  }
};

/* Submit / Draft wrappers — drive the in-flight spinner on the action buttons */
const isMainSubmitting = ref(false);
const isSavingDraft = ref(false);

const runSubmit = async () => {
  if (isMainSubmitting.value || isSavingDraft.value) return;
  isMainSubmitting.value = true;
  try {
    await (isEditOrder.value ? onEdit() : onSubmit());
  } finally {
    isMainSubmitting.value = false;
  }
};

const runDraft = async () => {
  if (isMainSubmitting.value || isSavingDraft.value) return;
  isSavingDraft.value = true;
  try {
    await (isEditOrder.value ? onDraftEdit() : onDraft());
  } finally {
    isSavingDraft.value = false;
  }
};

/* Upload File */
const onUploadDocx = (index: any, fileName: string, fileSize: string) => {
  if (fileCount.value === 0) {
    newFiles.value[index].file = orderFiles.value[index].document;
    newFiles.value[index].index = index;
    newFiles.value[index].file_name = fileName;
    newFiles.value[index].file_size = fileSize;
  } else {
    newFiles.value.push({
      file: orderFiles.value[index].document,
      index: index,
      file_name: fileName,
      file_size: fileSize,
    });
  }
  fileCount.value++;
  orders.value[index].remove_files = false;
};

const dynamicFieldPairs = (orderIndex: number) => {
  const flat: any[] = [];
  const triggeredOnField = formFields.value.find(
    (f: any) => f.field.model_field_name === 'order_triggered_on'
  );
  for (const f of formFields.value) {
    if (f.field.model_field_name === 'order_triggered_on') continue;
    flat.push({
      key: f.field.model_field_name,
      label: f.field.field_name,
      dataType: f.field.data_type,
      mandatory: f.is_mandatory,
    });
    if (
      f.field.model_field_name === 'order_triggered' &&
      orders.value[orderIndex]?.order_triggered &&
      triggeredOnField
    ) {
      flat.push({
        key: 'order_triggered_on',
        label: triggeredOnField.field.field_name,
        dataType: 'DATE',
        mandatory: triggeredOnField.is_mandatory,
      });
    }
  }
  const pairs: any[] = [];
  for (let i = 0; i < flat.length; i += 2) pairs.push(flat.slice(i, i + 2));
  return pairs;
};

onMounted(async () => {
  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.recurring_orders',
      to: { name: 'recurring-orders' },
    },
    {
      labelKey: 'orders.titles.new_recurring',
    },
  ]);

  /* Edit Order */
  isEditOrder.value = !!route.params.id;

  if (isEditOrder.value) {
    let orderID = route.params.id as string;
    orders.value = [];
    getRecurringInstructionById(orderID).then(response => {
      orders.value.push(response);
      orders.value.map(async (order: any) => {
        if (order.customer !== formData.customer) {
          router.push({ name: 'recurring-orders' });
        }
        editOrderId.value = order.id;
        formData.customer = order.customer;
        formData.project = order.project;
        formData.orderCount = 1;
        formData.name = order.name === null ? '' : order.name;
        formData.startDate = order.start_date;
        formData.frequency = order.frequency === null ? '' : order.frequency;
        formData.endsAfter = order.end_after === null ? 0 : order.end_after;
        // order.attachedDocument = order.files.length > 0 ? order.files[0].file : null;
      });
    });
  }
  /* Edit Order */
  await getCustomersOptions().then(response => {
    customersList.value = response;
    let getSelectedCustomerID = cookieService.get('selectedCustomerId');
    let customerId = getSelectedCustomerID ? JSON.parse(getSelectedCustomerID) : -1;
    if (customerId !== -1) formData.customer = customerId;
  });
});

const openNewSupplierModal = (currentIndex: number) => {
  isSupplierPopup.value = true;
  orderSupplierIndex.value = currentIndex;
  setFieldValue('customer', formData.customer);
};
const closeNewSupplierModal = () => {
  isSupplierPopup.value = false;
  resetForm();
};

const addNewSupplier = handleSubmit(async values => {
  try {
    const supplierResponse: any = await createSupplier({
      name: values.supplierName,
      supplier_number: values.supplierNumber,
      address: values.address,
      zip_code: values.zipCode!,
      city: values.city!,
      country: values.country!,
      phone_number: values.phone,
      email: values.supplierEmail,
      customer: formData.customer,
    });

    orders.value[orderSupplierIndex.value].supplier = supplierResponse.id;
    orders.value[orderSupplierIndex.value].order_no_supplier = supplierResponse.supplier_number;

    resetForm();
    closeNewSupplierModal();
    const selectInstance = Array.isArray(supplierSelectRef.value)
      ? supplierSelectRef.value[orderSupplierIndex.value]
      : supplierSelectRef.value;
    await selectInstance?.fetchSuppliers();
  } catch (error: any) {
    supplierCreatedSuccessfully.value = false;
    errorSupplierMessage.value =
      error?.response?.data?.errors?.[0]?.detail || error?.message || 'An error occurred';
  }
});

watch(country, async (selectedCountry: string | null) => {
  resetCity();
  if (selectedCountry) citiesList.value = await getCitiesListByCountry(selectedCountry);
});
</script>
